const CODE_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
const LETTERS = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
const DIGITS = "23456789";
const SYMBOLS = "!@#$%&*+-=?";
const PASSCODE_ALPHABET = `${LETTERS}${DIGITS}${SYMBOLS}`;
export const KDF_ITERATIONS = 310_000;

function randomIndex(length: number): number {
  if (!Number.isSafeInteger(length) || length <= 0 || length > 256) {
    throw new Error("Invalid random alphabet length");
  }
  const cutoff = Math.floor(256 / length) * length;
  const buffer = new Uint8Array(1);
  do {
    crypto.getRandomValues(buffer);
  } while (buffer[0] >= cutoff);
  return buffer[0] % length;
}

function randomFrom(alphabet: string): string {
  return alphabet[randomIndex(alphabet.length)];
}

export function generateSwarmCode(): string {
  return Array.from({ length: 8 }, () => randomFrom(CODE_ALPHABET)).join("");
}

export function normalizeSwarmCode(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
}

export function formatSwarmCode(value: string): string {
  const normalized = normalizeSwarmCode(value);
  return normalized.length > 4 ? `${normalized.slice(0, 4)}-${normalized.slice(4)}` : normalized;
}

export function generatePasscode(): string {
  const chars = [randomFrom(LETTERS), randomFrom(DIGITS), randomFrom(SYMBOLS)];
  while (chars.length < 8) chars.push(randomFrom(PASSCODE_ALPHABET));
  for (let index = chars.length - 1; index > 0; index -= 1) {
    const target = randomIndex(index + 1);
    [chars[index], chars[target]] = [chars[target], chars[index]];
  }
  return chars.join("");
}

export function isValidPasscode(value: string): boolean {
  return (
    value.length === 8 &&
    /^[A-Za-z0-9!@#$%&*+\-=?]{8}$/.test(value) &&
    /[A-Za-z]/.test(value) &&
    /[0-9]/.test(value) &&
    /[!@#$%&*+\-=?]/.test(value)
  );
}

export function randomSalt(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return bytesToBase64(bytes);
}

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

export function base64ToBytes(value: string): Uint8Array<ArrayBuffer> {
  if (!/^[A-Za-z0-9_-]+$/u.test(value) || value.length % 4 === 1) {
    throw new Error("Invalid base64url value");
  }
  const remainder = value.length % 4;
  const padded = value.replaceAll("-", "+").replaceAll("_", "/")
    + (remainder === 0 ? "" : "=".repeat(4 - remainder));
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

export interface DerivedSwarmCredentials {
  verifier: string;
  encryptionKey: CryptoKey;
}

export async function deriveSwarmCredentials(
  code: string,
  passcode: string,
  saltBase64: string,
  iterations = KDF_ITERATIONS,
): Promise<DerivedSwarmCredentials> {
  const material = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(`${normalizeSwarmCode(code)}:${passcode}`),
    "PBKDF2",
    false,
    ["deriveBits"],
  );
  const bits = new Uint8Array(
    await crypto.subtle.deriveBits(
      {
        name: "PBKDF2",
        hash: "SHA-256",
        salt: base64ToBytes(saltBase64),
        iterations,
      },
      material,
      512,
    ),
  );
  const verifier = bytesToBase64(bits.slice(0, 32));
  const encryptionKey = await crypto.subtle.importKey(
    "raw",
    bits.slice(32),
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"],
  );
  bits.fill(0);
  return { verifier, encryptionKey };
}

export interface CipherEnvelope {
  ciphertext: string;
  iv: string;
}

export async function encryptJson(
  key: CryptoKey,
  code: string,
  value: unknown,
): Promise<CipherEnvelope> {
  const iv = new Uint8Array(12);
  crypto.getRandomValues(iv);
  const plaintext = new TextEncoder().encode(JSON.stringify(value));
  const encrypted = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv,
      additionalData: new TextEncoder().encode(normalizeSwarmCode(code)),
      tagLength: 128,
    },
    key,
    plaintext,
  );
  return { ciphertext: bytesToBase64(new Uint8Array(encrypted)), iv: bytesToBase64(iv) };
}

export async function decryptJson<T>(
  key: CryptoKey,
  code: string,
  envelope: CipherEnvelope,
): Promise<T> {
  const decrypted = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: base64ToBytes(envelope.iv),
      additionalData: new TextEncoder().encode(normalizeSwarmCode(code)),
      tagLength: 128,
    },
    key,
    base64ToBytes(envelope.ciphertext),
  );
  return JSON.parse(new TextDecoder().decode(decrypted)) as T;
}
